import { useState, useEffect } from 'react';
import { NewsSearch } from './components/NewsSearch';
import { NewsResults } from './components/NewsResults';
import { Button } from '@/components/ui/button';
import Header from './components/Header';
import './App.css';
import { toast } from 'sonner'; // Import toast for feedback
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loader2 } from 'lucide-react';

interface NewsArticle {
  title: string;
  link: string;
  snippet: string;
  source: string;
  date: string;
  imageUrl?: string;
  content?: string;
  analysis?: {
    analysis: string;
    timestamp: string;
  };
}

function App() {
  const [newsArticles, setNewsArticles] = useState<NewsArticle[]>([]);
  const [analysisDialogOpen, setAnalysisDialogOpen] = useState(false);
  const [currentAnalyzedArticle, setCurrentAnalyzedArticle] = useState<NewsArticle | null>(null);
  const [analyzingArticle, setAnalyzingArticle] = useState<{ title: string; isAnalyzing: boolean }>({ title: '', isAnalyzing: false });

  // Load analyzed articles from localStorage on initial load
  useEffect(() => {
    const savedAnalyzed = localStorage.getItem('analyzedNews');
    if (savedAnalyzed) {
      try {
        // Also removing the call to setAnalyzedArticles here
      } catch (e) {
        console.error("Failed to parse analyzed news from localStorage", e);
        localStorage.removeItem('analyzedNews');
      }
    }
  }, []);

  const handleNewsResults = (results: NewsArticle[]) => {
    setNewsArticles(results);
  };

  const startAnalyzing = (article: NewsArticle) => {
    setAnalyzingArticle({ title: article.title, isAnalyzing: true });
  };

  const stopAnalyzing = () => {
    setAnalyzingArticle({ title: '', isAnalyzing: false });
  };

  const handleArticleAnalyzed = (analyzedArticle: NewsArticle) => {
    // Stop showing the analyzing notification
    stopAnalyzing();
    
    // Show the dialog with the analyzed article without saving
    setCurrentAnalyzedArticle(analyzedArticle);
    setAnalysisDialogOpen(true);
  };

  const handleCreateLead = (article: NewsArticle) => {
    const newLead = {
      id: article.link, // Use link as ID
      title: article.title,
      authority: article.source, // Map source to authority
      publication_date: article.date,
      country: 'N/A', // Set country to N/A for now
      description: article.snippet, // Map snippet to description
      ted_link: article.link, // Use link for ted_link
      status: 'new',
    };

    try {
      const savedLeadsString = localStorage.getItem('projectLeads');
      const currentLeads = savedLeadsString ? JSON.parse(savedLeadsString) : [];
      
      // Check if lead already exists
      if (currentLeads.some((lead: { id: string }) => lead.id === newLead.id)) {
        toast.info('Dieser Artikel ist bereits ein Lead.');
        return;
      }

      const updatedLeads = [...currentLeads, newLead];
      localStorage.setItem('projectLeads', JSON.stringify(updatedLeads));
      toast.success('Lead erfolgreich erstellt!'); // Success feedback
      setAnalysisDialogOpen(false);
    } catch (e) {
      console.error("Failed to save lead to localStorage", e);
      toast.error('Fehler beim Speichern des Leads.');
    }
  };

  const handleSaveAnalysis = () => {
    if (!currentAnalyzedArticle) return;

    // Save the article to localStorage
    let updatedAnalyzedArticles: NewsArticle[] = [];
    // Modify this section to not use setAnalyzedArticles
    
    // Get existing analyzed articles directly from localStorage
    const savedAnalyzed = localStorage.getItem('analyzedNews');
    let existingArticles: NewsArticle[] = [];
    
    if (savedAnalyzed) {
      try {
        existingArticles = JSON.parse(savedAnalyzed);
      } catch (e) {
        console.error("Failed to parse analyzed news from localStorage", e);
      }
    }
    
    // Update the articles array
    const index = existingArticles.findIndex(a => a.link === currentAnalyzedArticle.link);
    if (index >= 0) {
      // Replace existing analysis
      existingArticles[index] = currentAnalyzedArticle;
      updatedAnalyzedArticles = existingArticles;
    } else {
      // Add new analysis
      updatedAnalyzedArticles = [...existingArticles, currentAnalyzedArticle];
    }

    // Save to localStorage
    try {
      localStorage.setItem('analyzedNews', JSON.stringify(updatedAnalyzedArticles));
      toast.success('Analyse gespeichert');
      // Dispatch storage event so AnalysisPage updates if open
      window.dispatchEvent(new StorageEvent('storage', { key: 'analyzedNews' }));
    } catch (e) {
      console.error("Failed to save analyzed news to localStorage", e);
      toast.error("Fehler beim Speichern der Analyse.");
    }

    // Close the dialog after saving
    setAnalysisDialogOpen(false);
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4">
        <Header />
        <main className="py-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left column - Search */}
            <div className="lg:col-span-1">
              <NewsSearch onResultsFound={handleNewsResults} />
            </div>

            {/* Right columns - Results */}
            <div className="lg:col-span-2">
              <NewsResults
                articles={newsArticles}
                onArticleAnalyzed={handleArticleAnalyzed}
                onStartAnalyzing={startAnalyzing}
                onAnalysisError={stopAnalyzing}
              />
            </div>
          </div>
        </main>
      </div>

      {/* Analysis Results Dialog */}
      <Dialog open={analysisDialogOpen} onOpenChange={setAnalysisDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Analyse Ergebnisse</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-hidden flex flex-col">
            {currentAnalyzedArticle && (
              <>
                <div className="flex-none">
                  <h3 className="font-medium text-lg">{currentAnalyzedArticle.title}</h3>
                  <p className="text-sm text-muted-foreground mb-2">
                    {currentAnalyzedArticle.source} - {new Date(currentAnalyzedArticle.date).toLocaleDateString('de-DE')}
                  </p>
                </div>
                <div className="flex-1 overflow-y-auto mt-4 bg-muted rounded-lg p-4">
                  <h4 className="font-semibold mb-2 text-primary">Projektanalyse:</h4>
                  <div className="text-sm text-muted-foreground whitespace-pre-wrap">
                    {currentAnalyzedArticle.analysis?.analysis || 'Keine Analyse verfügbar'}
                  </div>
                </div>
                <div className="flex-none flex gap-4 justify-end mt-4">
                  <Button 
                    variant="outline" 
                    onClick={handleSaveAnalysis}
                  >
                    Speichern
                  </Button>
                  <Button 
                    variant="default" 
                    onClick={() => handleCreateLead(currentAnalyzedArticle)}
                  >
                    Lead erstellen
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Analyzing Notification */}
      {analyzingArticle.isAnalyzing && (
        <div className="fixed bottom-4 left-4 bg-background border shadow-md rounded-lg p-3 max-w-[300px] z-50 flex items-center gap-2 overflow-hidden">
          <Loader2 className="h-4 w-4 animate-spin text-primary flex-shrink-0" />
          <div className="text-sm overflow-hidden">
            <div className="font-medium">Analysiere Artikel</div>
            <div className="text-muted-foreground text-xs truncate w-full">{analyzingArticle.title}</div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
